import { Link } from "react-router-dom";
import { pickPhoto } from "@/lib/coachImage";
import { MapPin, Gauge } from "lucide-react";
import TrustBadges from "./TrustBadges";
import ResponsiveImage from "./ResponsiveImage";

const fmtPrice = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const fmtMiles = (n) => (n != null ? `${new Intl.NumberFormat("en-US").format(n)} mi` : "—");

export default function CoachCard({ listing, size = "default", priority = false, deferSourceUntilVisible = false }) {
  const img = pickPhoto(listing);
  return (
    <Link
      to={`/listing/${listing.id}`}
      className="group block bg-ivory border border-ivory-400 hover:border-bronze transition-colors"
      data-testid={`coach-card-${listing.id}`}
    >
      <div className={`relative overflow-hidden bg-ivory-100 ${size === "hero" ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
        <ResponsiveImage
          src={img}
          onError={(e) => { if (!e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = "1"; e.currentTarget.src = "/assets/coach-photos/_placeholder.svg"; } }}
          alt={listing.title}
          priority={priority}
          deferUntilVisible={deferSourceUntilVisible}
          // Card tiles never render wider than ~480px on mobile or ~600px on
          // desktop — cap the srcset so we don't ship 1600w bytes to a
          // 4-column grid.
          maxWidth={960}
          sizes={size === "hero"
            ? "(max-width: 768px) 100vw, 960px"
            : "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"}
          className="w-full h-full object-contain transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
        />
        {listing.featured && (
          <div
            className="absolute top-5 left-0 z-10 pl-4 pr-6 py-1.5 bg-copper text-ivory shadow-[0_2px_10px_rgba(198,161,91,0.35)]"
            style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 10px) 50%, 100% 100%, 0 100%)" }}
            data-testid={`featured-ribbon-${listing.id}`}
          >
            <span className="text-[10px] uppercase tracking-[0.28em] font-medium font-serif">Featured</span>
          </div>
        )}
        <div className="absolute top-4 right-4 flex flex-col gap-1.5 items-end">
          <TrustBadges listing={listing} compact />
        </div>
      </div>

      <div className="p-5 md:p-6 space-y-3">
        <div className="flex items-baseline justify-between gap-4">
          <div className="overline">{listing.converter}</div>
          <span className="text-xs text-ink-100 tracking-wider uppercase">
            {listing.chassis_model}
          </span>
        </div>
        <h3 className={`font-serif text-ink leading-tight ${size === "hero" ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`}>
          {listing.title}
        </h3>

        <div className="flex items-center gap-4 text-xs text-ink-100 pt-1">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            {listing.location}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Gauge className="w-3.5 h-3.5" />
            {fmtMiles(listing.mileage)}
          </span>
          <span className="text-ink-100">{listing.year}</span>
        </div>

        <div className="pt-4 flex items-end justify-between border-t border-ivory-400">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-ink-100 mt-3">
              {listing.listing_type === "auction" ? "Timed Bidding · Seller-Set Reserve"
                : listing.listing_type === "make_offer" ? "Make an Offer"
                : "Seller\u2019s Asking Price"}
            </div>
            <div className="price-serif text-2xl md:text-3xl text-bronze mt-1">
              {fmtPrice(listing.price)}
            </div>
          </div>
          <span className="text-[11px] uppercase tracking-[0.22em] text-copper group-hover:translate-x-1 transition-transform">
            View →
          </span>
        </div>
      </div>
    </Link>
  );
}
